import {
  Avatar,
  ButtonDS,
  DialogModalDS,
  SearchBarDS,
  Spacer,
  TableDS,
  Tag,
  TextCapitalized,
  triggerToastDS,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { energyGroups } from "@qivia/schemas-shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  driversListAsync,
  selectDriversList,
  selectDriversListStatus,
} from "../../drivers/driversSlice";
import { selectCompany } from "../../homeSlice";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDb,
  selectListAllVehiclesInDbStatus,
} from "../../vehicles/vehiclesSlice";
import { HomeChargingDriverRow } from "../linkyConsentsAPI";
import {
  addHomeChargingDriversAsync,
  linkyConsentsSlice,
  selectAddHomeChargingDriversStatus,
  selectLinkyConsents,
} from "../linkyConsentsSlice";
import { getHomeChargingAdditionRefreshDelay } from "../lib";
import "../../../../i18n/i18n";

const modalName = "linky_consents.modal.homeChargingDrivers";

export const HomeChargingDriversModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const { visible, onCloseModal } = props;
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const drivers = useAppSelector(selectDriversList);
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const addHomeChargingDriversStatus = useAppSelector(
    selectAddHomeChargingDriversStatus,
  );
  const linkyConsents = useAppSelector(selectLinkyConsents);

  const [selectedRows, setSelectedRows] = useState<HomeChargingDriverRow[]>([]);
  const [listDisplayed, setListDisplayed] = useState<HomeChargingDriverRow[]>(
    [],
  );

  useEffect(() => {
    if (company && driversListStatus === "idle") {
      void dispatch(driversListAsync(company.uuid));
    }
  }, [company, dispatch, driversListStatus]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  const driverRows = useMemo<HomeChargingDriverRow[]>(
    () =>
      drivers
        .filter(
          (driver) =>
            !linkyConsents.some((consent) => consent.uuid === driver.uuid),
        )
        .map((driver) => ({
          name: driver.name,
          sivEnergy:
            vehicles.find((vehicle) => vehicle.uuid === driver.vehicleUuid)
              ?.energy ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [drivers, vehicles, linkyConsents],
  );

  useEffect(() => {
    if (visible) {
      setSelectedRows([]);
      setListDisplayed(driverRows);
    }
  }, [visible, driverRows]);

  const onSubmit = useCallback(() => {
    if (selectedRows.length === 0) {
      return;
    }
    const selectedDriverNames = selectedRows.map((row) => row.name);
    const driversToActivate = drivers
      .filter((driver) => selectedDriverNames.includes(driver.name))
      .map((driver) => ({
        uuid: driver.uuid,
        vehicleUuid: driver.vehicleUuid,
        vehicleType:
          vehicles.find((vehicle) => vehicle.uuid === driver.vehicleUuid)
            ?.vehicleType ?? null,
      }));
    void dispatch(addHomeChargingDriversAsync(driversToActivate));
  }, [dispatch, drivers, selectedRows, vehicles]);

  useEffect(() => {
    if (addHomeChargingDriversStatus === "success") {
      triggerToastDS(t(`${modalName}.success`), "SUCCESS");
      dispatch(linkyConsentsSlice.actions.resetAddHomeChargingDriversStatus());
      dispatch(linkyConsentsSlice.actions.resetLinkyConsentsStatus());
      setTimeout(
        () => dispatch(linkyConsentsSlice.actions.resetLinkyConsentsStatus()),
        getHomeChargingAdditionRefreshDelay(selectedRows.length),
      );
      setSelectedRows([]);
      onCloseModal();
    } else if (addHomeChargingDriversStatus === "failed") {
      triggerToastDS(t(`${modalName}.error`), "ERROR");
      dispatch(linkyConsentsSlice.actions.resetAddHomeChargingDriversStatus());
    }
  }, [
    addHomeChargingDriversStatus,
    t,
    dispatch,
    onCloseModal,
    selectedRows.length,
  ]);

  const render =
    (row: HomeChargingDriverRow) => (key: keyof HomeChargingDriverRow) => {
      switch (key) {
        case "name": {
          const initials = row.name.split(" ").map((word) => word.charAt(0));
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={initials[0]}
                lastName={initials[initials.length - 1]}
              />
              <Spacer x={0.75} />
              {row.name}
            </StyledRow>
          );
        }
        case "sivEnergy": {
          const energy = energyGroups.find((group) =>
            group.acronymEnergies.some((acronym) => acronym === row.sivEnergy),
          )?.energy;
          if (!energy) {
            return <StyledVehicleType>-</StyledVehicleType>;
          }
          return (
            <StyledVehicleType>
              <Tag
                text={t(`${modalName}.vehicleType.${energy}`)}
                backgroundColor={
                  colors["colors/surfaces/background/background_level1"]
                }
                textColor={colors["colors/text/darkGrey"]}
              />
            </StyledVehicleType>
          );
        }
      }
    };

  const headers = { name: { text: "" }, sivEnergy: { text: "" } };

  return (
    <DialogModalDS
      visible={visible}
      width={44}
      onClose={onCloseModal}
      title={t(`${modalName}.title`) || ""}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t(`${modalName}.cancel`)}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t(`${modalName}.validate`)}
            format={"fill"}
            buttonType={"primary"}
            onClick={onSubmit}
            disabled={
              selectedRows.length === 0 ||
              addHomeChargingDriversStatus === "processing"
            }
          />
        ),
      }}
    >
      <StyledModalContainer>
        <StyledDescription>
          <TextCapitalized>{t(`${modalName}.subTitle`)}</TextCapitalized>
        </StyledDescription>
        <Spacer y={1} />
        <SearchBarDS<HomeChargingDriverRow>
          fullWidth
          values={driverRows}
          setFilterValues={setListDisplayed}
          placeholder={t("home.searchbar") ?? ""}
          keysToTranslate={["sivEnergy"]}
          findKeysToTranslate={(_key, value) =>
            `${modalName}.vehicleType.${
              energyGroups.find((group) =>
                group.acronymEnergies.some((acronym) => acronym === value),
              )?.energy
            }`
          }
        />
        <Spacer y={1} />
        <TableDS<keyof HomeChargingDriverRow, HomeChargingDriverRow>
          data={listDisplayed}
          headers={headers}
          render={render}
          height={25}
          rowsChecked={selectedRows}
          setRowsChecked={setSelectedRows}
          headerOverlayed={
            <StyledHeader>
              {t(`${modalName}.table.header`, { number: selectedRows.length })}
            </StyledHeader>
          }
          emptyContent={
            <StyledEmptyTable>
              <Spacer y={2} />
              <TextCapitalized>
                {driverRows.length === 0
                  ? t(`${modalName}.page.empty.title`)
                  : t(`${modalName}.page.emptySearch.text`)}
              </TextCapitalized>
              <Spacer y={2} />
            </StyledEmptyTable>
          }
        />
      </StyledModalContainer>
    </DialogModalDS>
  );
};

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledDescription = styled.div`
  ${typographies["Body/M"]};
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledVehicleType = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  ${typographies["Header/H3"]};
  color: ${colors["colors/text/black"]};
  align-items: center;
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;
